import React, { useMemo } from "react";

const RepairRequestTable = React.memo(
  ({
    reportData = [],
    totalcount = 0,
    handlePrint,
    data,
  }) => {
    /*
     * Backend response:
     *
     * data.document = {
     *   repairOrderNo,
     *   store,
     *   user,
     *   itemCount,
     *   items
     * }
     */
    const document = data?.document || data || {};

    const {
      repairOrderNo,
      store,
      user,
      itemCount,
      status: requestStatus,
      createdAt: requestCreatedAt,
    } = document;

    /*
     * Prefer reportData received from parent.
     * If parent does not separately set reportData,
     * use document.items directly.
     */
    const items = useMemo(() => {
      if (Array.isArray(reportData) && reportData.length > 0) {
        return reportData;
      }

      if (Array.isArray(document?.items)) {
        return document.items;
      }

      return [];
    }, [reportData, document?.items]);

    const formatDateDMY = (date) => {
      if (!date) return "N/A";

      const parsedDate = new Date(date);

      if (Number.isNaN(parsedDate.getTime())) {
        return "N/A";
      }

      const day = String(parsedDate.getDate()).padStart(2, "0");
      const month = String(parsedDate.getMonth() + 1).padStart(2, "0");
      const year = parsedDate.getFullYear();

      return `${day}/${month}/${year}`;
    };

    const getStatusBadgeClass = (status) => {
      switch (status) {
        case "recived":
        case "received":
        case "approved":
        case "close":
          return "bg-success";

        case "rejected":
        case "forceclosed":
          return "bg-danger";

        case "pending":
        case "open":
        case "approvedByAdmin":
        case "approvedBySuperAdmin":
          return "bg-warning text-dark";

        case "local":
        case "tranfer request":
          return "bg-info text-dark";

        default:
          return "bg-secondary";
      }
    };

    const getPoStatusBadgeClass = (poStatus) => {
      switch (poStatus) {
        case "generated":
          return "bg-success";

        case "pending":
        case "pendingForApproval":
        case "waitingForBoss":
          return "bg-warning text-dark";

        default:
          return "bg-secondary";
      }
    };

    const getDisplayQtyReceived = (item) => {
      return item?.qtyReceived ?? item?.qtyRecived ?? 0;
    };

    const tableRows = useMemo(() => {
      if (!items.length) {
        return (
          <tr>
            <td colSpan="24" className="text-center">
              No Repair Request Items Found.
            </td>
          </tr>
        );
      }

      return items.map((item, index) => (
        <tr key={item?._id || `${repairOrderNo}-${index}`}>
          <td>{index + 1}</td>

          {/* Request information */}
          <td>{repairOrderNo ?? "N/A"}</td>

          <td>{store?.storeCode || "N/A"}</td>

          <td className="scrollable-description">
            {store?.name || "N/A"}
          </td>

          <td>{user?.name || item?.user?.name || "N/A"}</td>

          {/* Inventory item information */}
          <td>{item?.inventoryPartNo || "N/A"}</td>

          <td className="scrollable-description">
            {item?.inventoryDescription || "N/A"}
          </td>

          <td>{item?.inventoryUnit || "N/A"}</td>

          {/* Repair/master-list information */}
          <td>
            {item?.masterlistPartNo ||
              item?.partNo ||
              "N/A"}
          </td>

          <td className="scrollable-description">
            {item?.masterlistDescription ||
              item?.description ||
              "N/A"}
          </td>

          <td>
            {item?.masterlistUnit ||
              item?.unit ||
              "N/A"}
          </td>

          {/* Quantities */}
          <td>{item?.qtyRequired ?? 0}</td>

          <td>{item?.approveQty ?? 0}</td>

          <td>{getDisplayQtyReceived(item)}</td>

          <td>{item?.damageQty ?? 0}</td>

          {/* Status */}
          <td>
            <span
              className={`badge ${getStatusBadgeClass(
                item?.status
              )}`}
            >
              {item?.status || "N/A"}
            </span>
          </td>

          <td>
            <span
              className={`badge ${getPoStatusBadgeClass(
                item?.poStatus
              )}`}
            >
              {item?.poStatus || "N/A"}
            </span>
          </td>

          <td>
            <span
              className={`badge ${
                item?.flag === "new"
                  ? "bg-primary"
                  : item?.flag === "remain"
                    ? "bg-warning text-dark"
                    : "bg-secondary"
              }`}
            >
              {item?.flag || "N/A"}
            </span>
          </td>

          {/* Approval information */}
          <td>
            {item?.approvedByAdmin?.name || "N/A"}
          </td>

          <td>
            {item?.approvedBySuperAdmin?.name || "N/A"}
          </td>

          <td>
            {item?.approvedByBoss?.name || "N/A"}
          </td>

          {/* Remarks */}
          <td className="scrollable-description">
            {item?.adminRemark || "N/A"}
          </td>

          <td className="scrollable-description">
            {item?.remark || "N/A"}
          </td>

          {/* Dates */}
          <td>{formatDateDMY(item?.createdAt)}</td>

          <td>{formatDateDMY(item?.updatedAt)}</td>
        </tr>
      ));
    }, [items, repairOrderNo, store, user]);

    return (
      <div className="filter cm-content-box box-primary">
        <div className="content-title d-flex justify-content-between align-items-center">
          <div className="cpa">
            <i className="fa fa-wrench me-2"></i>

            Repair Request Items

            <span className="badge bg-orange ms-2">
              {totalcount || itemCount || items.length}
            </span>

            {requestStatus && (
              <span
                className={`badge ms-2 ${getStatusBadgeClass(
                  requestStatus
                )}`}
              >
                {requestStatus}
              </span>
            )}
          </div>

          {items.length > 0 && (
            <button
              type="button"
              className="btn btn-primary"
              onClick={() => handlePrint("repair-request")}
            >
              Print
            </button>
          )}
        </div>

        <div className="cm-content-body form excerpt">
          <div className="card-body">
            {/* Request summary */}
            <div className="row mb-3">
              <div className="col-md-3 mb-2">
                <strong>Repair Order No:</strong>{" "}
                {repairOrderNo ?? "N/A"}
              </div>

              <div className="col-md-3 mb-2">
                <strong>Store:</strong>{" "}
                {store?.name || "N/A"}
              </div>

              <div className="col-md-3 mb-2">
                <strong>Store Code:</strong>{" "}
                {store?.storeCode || "N/A"}
              </div>

              <div className="col-md-3 mb-2">
                <strong>Requested By:</strong>{" "}
                {user?.name || "N/A"}
              </div>

              <div className="col-md-3 mb-2">
                <strong>Request Date:</strong>{" "}
                {formatDateDMY(requestCreatedAt)}
              </div>
            </div>

            <div className="scrolltable">
              <table className="table table-bordered table-striped table-hover">
                <thead className="sticky-table-header">
                  <tr>
                    <th>S.No</th>
                    <th>Repair Order No</th>
                    <th>Store Code</th>
                    <th>Store</th>
                    <th>Requested By</th>

                    <th>Inventory Part No</th>
                    <th>Inventory Description</th>
                    <th>Inventory Unit</th>

                    <th>Repair Part No</th>
                    <th>Repair Description</th>
                    <th>Repair Unit</th>

                    <th>Required Qty</th>
                    <th>Approved Qty</th>
                    <th>Received Qty</th>
                    <th>Damage Qty</th>

                    <th>Status</th>
                    <th>PO Status</th>
                    <th>Flag</th>

                    <th>Approved By PM</th>
                    <th>Approved By Central Team</th>
                    <th>Approved By HO</th>

                    <th>Admin Remark</th>
                    <th>Remark</th>

                    <th>Created Date</th>
                    <th>Updated Date</th>
                  </tr>
                </thead>

                <tbody>{tableRows}</tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    );
  }
);

RepairRequestTable.displayName = "RepairRequestTable";

export default RepairRequestTable;